/*
 * This file is part of the Companion project
 * Copyright (c) 2018 Bitfocus AS
 * Authors: William Viker <william@bitfocus.io>, Håkon Nessjøen <haakon@bitfocus.io>, Dorian Meid <meid@backstage.org>
 *
 * This program is free software.
 * You should have received a copy of the MIT licence as well as the Bitfocus
 * Individual Contributor License Agreement for companion along with
 * this program.
 *
 * You can be released from the requirements of the license by purchasing
 * a commercial license. Buying such a license is mandatory as soon as you
 * develop commercial activities involving the Companion software without
 * disclosing the source code of your own applications.
 *
 */

/// <reference lib="dom" />

import type QuickLRU from 'quick-lru'
import type { MinimalLogger } from '../Logger.js'
import type { ButtonGraphicsTextDrawElement } from '../Model/StyleLayersModel.js'
import { resolveFontName } from './Fonts.js'
import {
	computeTextLayout,
	findBestFontSize,
	MIN_FONT_SIZE_FRACTION,
	resolveFontSizeBounds,
	segmentTextToUnicodeChars,
	type TextLayoutResult,
} from './TextParser.js'
import type { DrawBounds, HorizontalAlignment, LineOrientation, VerticalAlignment } from './Util.js'

/**
 * Cache for text layout computations (also memoises font line-box ratios, keyed separately).
 */
export type TextLayoutCache = QuickLRU<string, TextLayoutResult | number>

export type PointXY = [x: number, y: number]

export interface LineStyle {
	/**
	 * Line color in CSS color string
	 */
	color: string
	/**
	 * Line width in pixels
	 */
	width: number
	/**
	 * Line cap style (defaults to 'butt')
	 */
	cap?: CanvasLineCap
}

export interface DrawAlignedTextOptions {
	/** whether to allow the font size to shrink to fit the box (default true) */
	allowShrink?: boolean
	/** horizontal alignment left, center, right (default center) */
	halign?: HorizontalAlignment
	/** vertical alignment top, center, bottom (default center) */
	valign?: VerticalAlignment
	/** optional outline style, if not provided there will be no outline */
	outlineStyle?: LineStyle
	/** optional font family, if not provided the default font will be used */
	font?: ButtonGraphicsTextDrawElement['font']
	/** font weight (default 'normal') */
	weight?: ButtonGraphicsTextDrawElement['weight']
	/** render italic (default false) */
	italic?: boolean
	/** render an underline (default false) */
	underline?: boolean
	/** render a strikethrough (default false) */
	strikethrough?: boolean
}

/** Take a limited view of CompanionImageContext2D, based on what skia canvas supports */
export type CompanionImageContext2D = Omit<
	CanvasRenderingContext2D,
	'drawImage' | 'createPattern' | 'drawFocusIfNeeded' | 'scrollPathIntoView' | 'canvas'
>

/**
 * A simple image pool, to allow for using temporary images for compositing purposes
 * This should minimise GC pressure by reusing the same image object
 */
export abstract class ImagePoolBase<TImage extends ImageBase<any>> {
	#pool: TImage[] = []

	abstract createImage(textLayoutCache: TextLayoutCache | null): TImage

	async usingImage<T>(textLayoutCache: TextLayoutCache | null, fcn: (img: TImage) => Promise<T>): Promise<T> {
		const image = this.#pool.pop() || this.createImage(textLayoutCache)

		try {
			image.clear()

			return await fcn(image)
		} finally {
			this.#pool.push(image)
		}
	}
}

/**
 * Class for generating an image and rendering some content to it
 */
export abstract class ImageBase<TDrawImageType extends { width: number; height: number }> {
	protected readonly logger: MinimalLogger

	readonly #imagePool: ImagePoolBase<ImageBase<TDrawImageType>>

	protected readonly context2d: CompanionImageContext2D

	readonly width: number
	readonly height: number

	readonly #textLayoutCache: TextLayoutCache | null

	/**
	 * Get the canvas image object, so that it can be drawn to another ImageBase instance
	 */
	protected abstract get canvasImage(): TDrawImageType

	/**
	 * Create an image
	 * @param width the width of the image in integer
	 * @param height the height of the image in integer
	 * @param oversampling a factor of how much more pixels the image should have in width and height
	 */
	protected constructor(
		logger: MinimalLogger,
		pool: ImagePoolBase<ImageBase<TDrawImageType>>,
		context2d: CompanionImageContext2D,
		width: number,
		height: number,
		textLayoutCache: TextLayoutCache | null
	) {
		this.logger = logger
		this.#imagePool = pool
		this.context2d = context2d
		this.width = width
		this.height = height
		this.#textLayoutCache = textLayoutCache
	}

	/**
	 * Draw to a temporary image layer, before compositing it to the main image with a given alpha
	 * This allows for complex drawing sequences to be flattened with the correct combined alpha
	 */
	async usingTemporaryLayer(
		compositeAlpha: number,
		fcn: (img: ImageBase<TDrawImageType>) => Promise<void>
	): Promise<void> {
		return this.#imagePool.usingImage(this.#textLayoutCache, async (img) => {
			// Setup the temporary layer to use the same transform
			img.context2d.setTransform(this.context2d.getTransform())

			await fcn(img)

			// Flatten the layer straight back at the given alpha. The transform is already baked into the
			// layer's pixels, so bypass our current transform and blit it 1:1 in device space.
			await this.usingAlpha(compositeAlpha, async () => {
				this.context2d.save()
				this.context2d.resetTransform()
				this.drawImage(
					img.canvasImage,
					0,
					0,
					img.canvasImage.width,
					img.canvasImage.height,
					0,
					0,
					this.canvasImage.width,
					this.canvasImage.height
				)
				this.context2d.restore()
			})
		})
	}

	/**
	 * Perform some drawing with a given alpha.
	 * Note: This affects the whole canvas drawing operations, it should contain a single operation otherwise the composition of each draw will not correctly combine colors
	 */
	async usingAlpha(alpha: number, fcn: () => Promise<void>): Promise<void> {
		const oldAlpha = this.context2d.globalAlpha
		this.context2d.globalAlpha *= alpha

		try {
			await fcn()
		} finally {
			this.context2d.globalAlpha = oldAlpha
		}
	}

	/**
	 * Perform some drawing with a given rotation about the midpoint of the drawBounds.
	 */
	async usingRotation(drawBounds: DrawBounds, rotation: number, fcn: () => Promise<void>): Promise<void> {
		this.context2d.save()

		const centerX = drawBounds.x + drawBounds.width / 2
		const centerY = drawBounds.y + drawBounds.height / 2

		this.context2d.translate(centerX, centerY)
		this.context2d.rotate((rotation * Math.PI) / 180)
		this.context2d.translate(-centerX, -centerY)

		try {
			await fcn()
		} finally {
			this.context2d.restore()
		}
	}

	/**
	 * Perform some drawing optionally clipped to a rectangular region (pass null to not clip). The clip is
	 * baked into device space here, so it keeps masking every subsequent draw on this context.
	 */
	async usingClip<T>(clipBounds: DrawBounds | null, fcn: () => Promise<T>): Promise<T> {
		this.context2d.save()

		if (clipBounds) {
			this.context2d.beginPath()
			this.context2d.rect(clipBounds.x, clipBounds.y, clipBounds.width, clipBounds.height)
			this.context2d.clip()
		}

		try {
			return await fcn()
		} finally {
			this.context2d.restore()
		}
	}

	/**
	 * Clear the image, resetting to a fully transparent and empty state
	 */
	clear(): void {
		// Fully reset the context/canvas
		this.context2d.reset()

		// reset() also discards the transform (eg oversampling scale) and any other initial
		// context state, so re-apply it
		this.initialiseContext()
	}

	/**
	 * Apply the initial context state (eg the oversampling transform) that must survive a `reset()`.
	 */
	protected abstract initialiseContext(): void

	/**
	 * Draw an image loaded with one of the load abstract load functions
	 */
	protected abstract drawImage(
		image: TDrawImageType,
		sx: number,
		sy: number,
		sw: number,
		sh: number,
		dx: number,
		dy: number,
		dw: number,
		dh: number
	): void

	/**
	 * Load an image from a base64 string
	 * This will later be drawn with drawImage
	 */
	protected abstract loadBase64Image(base64Image: string): Promise<TDrawImageType>

	/**
	 * Prepare a pixel buffer for drawing
	 * This will later be drawn with drawImage
	 */
	protected abstract loadPixelBuffer(data: Uint8Array, width: number, height: number): TDrawImageType | null

	/**
	 * fills the whole image with a color
	 * @param color CSS color string
	 * @returns success
	 */
	fillColor(color: string): boolean {
		return this.box(0, 0, this.width, this.height, color)
	}

	/**
	 * draws a line between two given points
	 */
	line(x1: number, y1: number, x2: number, y2: number, style: LineStyle): void {
		const oldLineCap = this.context2d.lineCap
		this.context2d.lineWidth = style.width ?? 1
		this.context2d.strokeStyle = style.color
		this.context2d.lineCap = style.cap ?? 'butt'
		try {
			this.context2d.beginPath()
			this.context2d.moveTo(x1, y1)
			this.context2d.lineTo(x2, y2)
			this.context2d.closePath()
			this.context2d.stroke()
		} finally {
			this.context2d.lineCap = oldLineCap
		}
	}

	/**
	 * draws a horizontal line at given height from top
	 */
	horizontalLine(y: number, style: LineStyle): void {
		return this.line(0, y, this.width, y, style)
	}

	/**
	 * draws a vertical line at given distance from left
	 * @returns success
	 */
	verticalLine(x: number, style: LineStyle): void {
		return this.line(x, 0, x, this.height, style)
	}

	/**
	 * draws a box with optional fill color and optional outline
	 * @param x1
	 * @param y1
	 * @param x2
	 * @param y2
	 * @param color CSS string fill color, unfilled if undefined
	 * @param strokeColor CSS string line color, no line if undefined
	 * @param lineWidth line width defaults to 1
	 * @param lineOrientation defaults to 'inside'
	 * @returns something has been drawn
	 */
	box(
		x1: number,
		y1: number,
		x2: number,
		y2: number,
		fillColor?: string,
		lineStyle?: LineStyle,
		lineOrientation: LineOrientation = 'inside',
		cornerRadius = 0
	): boolean {
		if (x2 == x1 || y2 == y1) return false
		let didDraw = false
		if (fillColor) {
			this.context2d.fillStyle = fillColor
			if (cornerRadius > 0) {
				this.context2d.beginPath()
				this.context2d.roundRect(x1, y1, x2 - x1, y2 - y1, cornerRadius)
				this.context2d.fill()
			} else {
				this.context2d.fillRect(x1, y1, x2 - x1, y2 - y1)
			}
			didDraw = true
		}
		if (lineStyle) {
			didDraw = this.boxLine(x1, y1, x2, y2, lineStyle, lineOrientation, cornerRadius) || didDraw
		}

		return didDraw
	}

	/**
	 * draws a circle with optional fill color and optional outline
	 * @param x
	 * @param y
	 * @param radiusX
	 * @param radiusY
	 * @param startAngle in radians
	 * @param endAngle in radians
	 * @param color CSS string fill color, unfilled if undefined
	 * @param strokeColor CSS string line color, no line if undefined
	 * @param lineWidth line width defaults to 1
	 * @param lineOrientation defaults to 'inside'
	 * @returns something has been drawn
	 */
	circle(
		x: number,
		y: number,
		radiusX: number,
		radiusY: number,
		startAngle: number = 0,
		endAngle: number = Math.PI * 2,
		drawSlice?: boolean,
		fillColor?: string,
		lineStyle?: LineStyle,
		borderOnlyArc?: boolean,
		lineOrientation: LineOrientation = 'inside'
	): boolean {
		if (radiusX <= 0 || radiusY <= 0) return false

		// Special case for full circle, it doesn't draw if both points are the same and non-default
		if (Math.abs(startAngle - endAngle) <= 0.001) {
			startAngle = 0
			endAngle = 2 * Math.PI
			drawSlice = false
		}

		let didDraw = false
		if (fillColor) {
			this.context2d.fillStyle = fillColor
			// this.context2d.fillRect(x1, y1, x2 - x1, y2 - y1)
			this.context2d.beginPath()
			this.context2d.ellipse(x, y, radiusX, radiusY, 0, startAngle, endAngle)

			if (drawSlice) {
				// Draw as a pie slice, connecting to center
				this.context2d.lineTo(x, y)
				this.context2d.closePath()
			}

			this.context2d.fill()
			didDraw = true
		}
		if (lineStyle) {
			let borderRadiusX = radiusX
			let borderRadiusY = radiusY

			const lineWidth = lineStyle.width ?? 1
			if (lineWidth > 0) {
				// Offset the radius to account for line position
				const halfline = lineWidth / 2
				switch (lineOrientation) {
					case 'inside':
						borderRadiusX -= halfline
						borderRadiusY -= halfline
						break
					case 'outside':
						borderRadiusX += halfline
						borderRadiusY += halfline
						break
				}

				this.context2d.lineWidth = lineWidth
				this.context2d.strokeStyle = lineStyle.color

				this.context2d.beginPath()
				this.context2d.ellipse(x, y, borderRadiusX, borderRadiusY, 0, startAngle, endAngle)

				if (!borderOnlyArc) {
					if (drawSlice) {
						// Draw as a pie slice, connecting to center
						this.context2d.lineTo(x, y)
					}
					this.context2d.closePath()
				}

				this.context2d.stroke()
				didDraw = true
			}
		}

		return didDraw
	}

	/**
	 * Draws a stroked arc (no fill). Used by the ring gauge renderer.
	 * @param x center x
	 * @param y center y
	 * @param radius arc radius in pixels
	 * @param startAngle in radians
	 * @param endAngle in radians
	 * @param anticlockwise whether to draw counter-clockwise
	 * @param lineStyle stroke style and width
	 */
	arcStroke(
		x: number,
		y: number,
		radius: number,
		startAngle: number,
		endAngle: number,
		anticlockwise: boolean,
		lineStyle: LineStyle
	): void {
		if (radius <= 0 || lineStyle.width <= 0) return
		const oldLineCap = this.context2d.lineCap
		this.context2d.beginPath()
		this.context2d.arc(x, y, radius, startAngle, endAngle, anticlockwise)
		this.context2d.strokeStyle = lineStyle.color
		this.context2d.lineWidth = lineStyle.width
		this.context2d.lineCap = lineStyle.cap ?? 'butt'
		try {
			this.context2d.stroke()
		} finally {
			this.context2d.lineCap = oldLineCap
		}
	}

	/**
	 * Draws an outline rectangle
	 * @param x1 position of left edge
	 * @param y1 position of top edge
	 * @param x2 position of right edge
	 * @param y2 position of bottom edge
	 * @param color color string
	 * @param lineWidth line width
	 * @param  lineOrientation direction of lines in regard to the edges
	 * @returns returns true if a visible rectangle has been drawn
	 */
	boxLine(
		x1: number,
		y1: number,
		x2: number,
		y2: number,
		lineStyle: LineStyle,
		lineOrientation: LineOrientation = 'inside',
		cornerRadius = 0
	): boolean {
		const lineWidth = lineStyle.width ?? 1
		if (lineWidth <= 0) return false

		const halfline = lineWidth / 2
		// Keep the stroked corners concentric with the fill by shifting the radius with the edge (only
		// when there is a radius — a square box must stay square regardless of border orientation)
		let radius = cornerRadius
		switch (lineOrientation) {
			case 'inside':
				x1 += halfline
				y1 += halfline
				x2 -= halfline
				y2 -= halfline
				if (cornerRadius > 0) radius = Math.max(0, cornerRadius - halfline)
				break
			case 'outside':
				x1 -= halfline
				y1 -= halfline
				x2 += halfline
				y2 += halfline
				if (cornerRadius > 0) radius = cornerRadius + halfline
				break
		}

		this.context2d.lineWidth = lineWidth
		this.context2d.strokeStyle = lineStyle.color
		if (radius > 0) {
			this.context2d.beginPath()
			this.context2d.roundRect(x1, y1, x2 - x1, y2 - y1, radius)
			this.context2d.stroke()
		} else {
			this.context2d.strokeRect(x1, y1, x2 - x1, y2 - y1)
		}

		return true
	}

	/**
	 * Draws an image to the canvas from a buffer
	 * the image can be fitted and cropped to the canvas in many variations
	 * @param data buffer with the raw image data
	 * @param xStart left position where to place the image
	 * @param yStart top position where to place the image
	 * @param width width of the bounding box where to place the image
	 * @param height height of the bounding box where to place the image
	 * @param halign horizontal alignment of the image in the bounding box (defaults to center)
	 * @param valign vertical alignment of the image in the bounding box (defaults to center)
	 * @param scale the size factor of the image. Number scales by specified amount, fill scales to fill the bounding box neglecting aspect ratio, crop scales to fill the bounding box and crop if necessary, fit scales to fit the bounding box with the longer side
	 */
	async drawBase64Image(
		base64Image: string,
		xStart = 0,
		yStart = 0,
		width = 72,
		height = 72,
		halign: HorizontalAlignment = 'center',
		valign: VerticalAlignment = 'center',
		scale: number | 'crop' | 'fill' | 'fit' | 'fit_or_shrink' = 1
	): Promise<true | false | null> {
		// skia-canvas decodes a base64 string up to a stray `=`, but browsers reject the whole thing.
		// Trim to valid base64 so both renderers behave consistently.
		base64Image = trimBase64ImageToValid(base64Image)

		if (!base64Image || base64Image.length <= 40) {
			// No image data. This is a bit cautious of a threshold, as empty buffers cause the canvas to crash
			return null
		}

		let canvasImage: TDrawImageType | undefined

		try {
			canvasImage = await this.loadBase64Image(base64Image)
		} catch (e) {
			this.logger.error('Error loading image', e)
			return false
		}

		const imageWidth = canvasImage.width
		const imageHeight = canvasImage.height

		let calculatedScale = 1
		let scaledImageWidth = imageWidth
		let scaledImageHeight = imageHeight

		if (scale === 'fit_or_shrink') {
			if (imageWidth <= width && imageHeight <= height) {
				// If image is smaller than the button, don't scale it
				scale = 1
			} else {
				// Otherwise shrink to fit
				scale = 'fit'
			}
		}

		if (scale === 'fit') {
			const scaleMin = Math.min(width / imageWidth, height / imageHeight)
			const scaleMax = Math.max(width / imageWidth, height / imageHeight)
			calculatedScale = scaleMax < scaleMin ? scaleMax : scaleMin
			scaledImageWidth = imageWidth * calculatedScale
			scaledImageHeight = imageHeight * calculatedScale
		} else if (scale === 'crop') {
			calculatedScale = Math.max(width / imageWidth, height / imageHeight)
			scaledImageWidth = imageWidth * calculatedScale
			scaledImageHeight = imageHeight * calculatedScale
		} else if (typeof scale === 'number') {
			if (scale <= 0) {
				this.logger.warn('image scale is zero or negative, abort drawing of image')
				return null
			}
			calculatedScale = scale
			scaledImageWidth = imageWidth * calculatedScale
			scaledImageHeight = imageHeight * calculatedScale
		} else {
			// if there is none of the aspect ration retaining scales, do the 'fill'-type
			scaledImageWidth = width
			scaledImageHeight = height
		}

		if (scaledImageWidth < 1 || scaledImageHeight < 1) {
			this.logger.warn('image width or height after scaling is less then one pixel, abort drawing of image')
			return null
		}

		// set default transformation values at 'fill'-type
		const source = { x: 0, y: 0, w: imageWidth, h: imageHeight }
		const destination = { x: xStart, y: yStart, w: width, h: height }

		if (scaledImageWidth > width) {
			//image is broader than drawing pane
			source.w = width / calculatedScale
			switch (halign) {
				case 'center':
					source.x = (imageWidth - source.w) / 2
					break
				case 'right':
					source.x = imageWidth - source.w
					break
			}
		} else if (scaledImageWidth < width) {
			// image is narrower than drawing pane
			destination.w = scaledImageWidth
			switch (halign) {
				case 'center':
					destination.x += (width - scaledImageWidth) / 2
					break
				case 'right':
					destination.x += width - scaledImageWidth
					break
			}
		}

		if (scaledImageHeight > height) {
			// image is taller than drawing pane
			source.h = height / calculatedScale
			switch (valign) {
				case 'center':
					source.y = (imageHeight - source.h) / 2
					break
				case 'bottom':
					source.y = imageHeight - source.h
					break
			}
		} else if (scaledImageHeight < height) {
			// image is smaller than drawing pane
			destination.h = scaledImageHeight
			switch (valign) {
				case 'center':
					destination.y += (height - scaledImageHeight) / 2
					break
				case 'bottom':
					destination.y += height - scaledImageHeight
					break
			}
		}

		this.drawImage(
			canvasImage,
			source.x,
			source.y,
			source.w,
			source.h,
			destination.x,
			destination.y,
			destination.w,
			destination.h
		)
		return true as const
	}

	#sanitiseText(text: string | undefined): string {
		if (text === undefined) return ''

		// If there is a null character in the string, cut it off
		const nullIndex = text.indexOf('\0')
		if (nullIndex == -1) return text
		return text.substring(0, nullIndex)
	}

	/**
	 * draws a single line of left aligned text
	 * the line length is not wrapped or limited and may extend beyond the canvas
	 * @param x left position where to start the line
	 * @param y top position where to start the line
	 * @param text
	 * @param color CSS color string
	 * @param fontsize Em height
	 * @param dummy Don't actually draw anything just return the text width
	 * @returns width of the line
	 */
	drawTextLine(x: number, y: number, text: string, color: string, fontsize: number, dummy = false): number {
		text = this.#sanitiseText(text)

		if (text === undefined || text.length == 0) return 0

		if (isNaN(fontsize)) return 0
		if (fontsize < 3) return 0

		this.context2d.font = `${fontsize}px ${resolveFontName('companion-sans')}`

		const metrics = this.context2d.measureText(text)

		if (!dummy) {
			this.context2d.textAlign = 'left'
			this.context2d.fillStyle = color
			this.context2d.fillText(text, x, y + Math.round(metrics.fontBoundingBoxAscent))
		}

		return metrics.width
	}

	/**
	 * draws a single line of aligned text, doesn't care for line breaks or length
	 * @param x horizontal value of the alignment point
	 * @param y vertical value of the alignment point
	 * @param text
	 * @param color CSS color string
	 * @param fontsize Em height
	 * @param halignment defaults to 'center'
	 * @param valignment defaults to 'center'
	 * @returns the width of the line
	 */
	drawTextLineAligned(
		x: number,
		y: number,
		text: string,
		color: string,
		fontsize: number,
		halignment: HorizontalAlignment = 'center',
		valignment: VerticalAlignment = 'center',
		weight: 'normal' | 'bold' = 'normal'
	): number {
		text = this.#sanitiseText(text)

		if (text === undefined || text.length == 0) return 0
		if (halignment != 'left' && halignment != 'center' && halignment != 'right') halignment = 'left'
		if (weight != 'normal' && weight != 'bold') weight = 'normal'

		this.context2d.font = `${weight} ${fontsize}px ${resolveFontName('companion-sans')}`
		this.context2d.fillStyle = color
		this.context2d.textAlign = halignment

		const metrics = this.context2d.measureText(text)

		let vOffset = 0
		switch (valignment) {
			case 'top':
				vOffset = metrics.fontBoundingBoxAscent
				break

			case 'center':
				vOffset = metrics.fontBoundingBoxAscent / 2
				break

			case 'bottom':
				vOffset = metrics.fontBoundingBoxDescent * -1
				break
		}

		this.context2d.fillText(text, x, y + vOffset)

		return metrics.width
	}

	/**
	 * Measure a font's line-box ratio: (fontBoundingBoxAscent + fontBoundingBoxDescent) / em.
	 * The ratio is size-independent, so a desired line-box height can be converted to an em size
	 * via `em = desiredLineBox / ratio`. Used so that a text size of 100% makes the line box fill
	 * the box height (making vertical alignment a no-op at 100%), correctly per-font rather than
	 * assuming a fixed ratio.
	 */
	getFontLineBoxRatio(
		font: DrawAlignedTextOptions['font'],
		weight: DrawAlignedTextOptions['weight'],
		italic: boolean
	): number {
		const fontSpec = `${italic ? 'italic ' : ''}${weight === 'bold' ? 'bold ' : ''}100px ${resolveFontName(font)}`
		const cacheKey = `linebox:${fontSpec}`
		const cached = this.#textLayoutCache?.get(cacheKey)
		if (typeof cached === 'number') return cached

		const previousFont = this.context2d.font
		this.context2d.font = fontSpec
		const metrics = this.context2d.measureText('A')
		this.context2d.font = previousFont
		const rawRatio = (metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent) / 100
		const ratio = rawRatio > 0 ? rawRatio : 1.2

		this.#textLayoutCache?.set(cacheKey, ratio)
		return ratio
	}

	/**
	 * Draws aligned text in an boxed area.
	 * @param x bounding box top left horizontal value
	 * @param y bounding box top left vertical value
	 * @param w bounding box width
	 * @param h bounding box height
	 * @param text the text to draw
	 * @param color CSS color string
	 * @param fontsize height of font, either pixels or 'auto'
	 * @param options optional styling: allowShrink, alignment, outline, font, weight and character styles
	 */
	drawAlignedText(
		x: number,
		y: number,
		w: number,
		h: number,
		text: string,
		color: string,
		fontsize: number,
		options: DrawAlignedTextOptions = {}
	): void {
		const {
			allowShrink = true,
			halign = 'center',
			valign = 'center',
			outlineStyle,
			font,
			weight = 'normal',
			italic = false,
			underline = false,
			strikethrough = false,
		} = options

		if (w <= 0 || h <= 0) return

		let displayTextStr = this.#sanitiseText(text).toString().trim() // remove leading and trailing spaces for display
		if (!displayTextStr) return

		displayTextStr = displayTextStr.replaceAll('\r\n', '\n') // we only want \n as a linebreak
		displayTextStr = displayTextStr.replaceAll('\\n', '\n') // users can add deliberate line breaks, let's replace it with a real line break
		displayTextStr = displayTextStr.replaceAll('\\r', '\n') // users can add deliberate line breaks, let's replace it with a real line break
		displayTextStr = displayTextStr.replaceAll('\\t', '\t') // users can add deliberate tabs, let's replace it with a real tab

		// Resolution-independent upper bound: capacity ≈ (w/h) / (fontFrac² × charAspect).
		// When shrinking, the minimum font fraction is the absolute limit; when fixed, use the
		// configured size (clamped to its minimum of h/24), giving a much tighter bound.
		const effectiveFontFrac = allowShrink ? MIN_FONT_SIZE_FRACTION : Math.min(Math.max(fontsize / h, 1 / 24), 1)
		const maxPossibleChars = Math.ceil(w / h / (effectiveFontFrac * effectiveFontFrac * 0.3))

		const { displayTextChars, displayTextCharsStr, wasTruncated } = segmentTextToUnicodeChars(
			displayTextStr,
			maxPossibleChars
		)

		// Normalize layout decisions to a fixed reference height (72 px) so that font-size selection
		// and line-breaking use identical absolute pixel measurements regardless of actual canvas size.
		const NORM_H = 72
		// Sub-pixel precision of the shrink-to-fit search, at the normalized scale
		const SIZE_SEARCH_GRID = 0.25
		const normScale = NORM_H / h
		const normW = Math.round(w * normScale)
		// When fontsize === h (signals "shrink up to the full height"), normFontsize === NORM_H — preserved.
		const normFontsize = fontsize * normScale
		const upScale = h / NORM_H
		const fontNameStr = resolveFontName(font)

		// CSS font shorthand prefix: `font-style font-weight` (skia/browsers synthesize faux italic/bold).
		// Must be part of the measured spec too, as it affects glyph widths (and the layout cache key).
		const fontStylePrefix = `${italic ? 'italic ' : ''}${weight === 'bold' ? 'bold ' : ''}`

		// Layout at a normalized size, cached by normalized dims (shared across same-aspect canvases).
		// `needComplete` forces a full layout for rendering; otherwise a fast early-exit probe is used,
		// whose only reliable output is `fits`. Only cache render-safe layouts: an early-exit probe is
		// complete only when it fits, so non-fitting probes are skipped.
		const getNormLayout = (size: number, needComplete: boolean): TextLayoutResult => {
			const normFontSpec = `${fontStylePrefix}${size}px/${size * 1.1}px ${fontNameStr}`
			const cacheKey = `${normFontSpec}:${normW}:${NORM_H}:${displayTextCharsStr}`
			const cachedLayout = this.#textLayoutCache?.get(cacheKey)
			if (typeof cachedLayout === 'object') return cachedLayout

			const layout = computeTextLayout(this.context2d, normW, NORM_H, displayTextChars, normFontSpec, !needComplete)
			if (needComplete || layout.fits) this.#textLayoutCache?.set(cacheKey, layout)
			return layout
		}

		const bounds = resolveFontSizeBounds(NORM_H, normFontsize, allowShrink)

		// Pick the normalized font size, keeping the layout the search found so it needn't be recomputed
		let usedNormSize: number
		let fittingLayout: TextLayoutResult | undefined
		if (!allowShrink) {
			usedNormSize = bounds.max // fixed size, render regardless of fit
		} else if (wasTruncated) {
			usedNormSize = bounds.min // hit the char cap, only the smallest size could fit
		} else {
			// Smooth shrink-to-fit. The search accepts fitting sizes in increasing order, so the last one
			// is the winner — capture its (complete, since it fits) layout to reuse below.
			usedNormSize = findBestFontSize(bounds, SIZE_SEARCH_GRID, (size) => {
				const layout = getNormLayout(size, false)
				if (layout.fits) fittingLayout = layout
				return layout.fits
			})
		}

		// The fallbacks (fixed/truncated/nothing-fit) weren't probed, so this computes once, not extra
		const normLayout = fittingLayout ?? getNormLayout(usedNormSize, true)

		// Scale the normalized layout up to the actual canvas dimensions for rendering
		const actualSize = usedNormSize * upScale
		const textLayout: TextLayoutResult = {
			fontDefinition: `${fontStylePrefix}${actualSize}px/${actualSize * 1.1}px ${fontNameStr}`,
			lines: normLayout.lines,
			measuredLineHeight: normLayout.measuredLineHeight * upScale,
			measuredAscent: normLayout.measuredAscent * upScale,
			totalHeight: normLayout.totalHeight * upScale,
			fits: normLayout.fits,
		}

		// Perform the draw
		this.#drawTextLayout(x, y, w, h, textLayout, color, halign, valign, outlineStyle, underline, strikethrough)
	}

	/**
	 * Draw text using a computed layout  
	 * when the text fits into the area, alignment will be trivial. When text overflows the line break has precedence. That means text should be broken into hopefully fitting lines (even inside of words) and then those lines are aligned.
	 */
	#drawTextLayout(
		x: number,
		y: number,
		w: number,
		h: number,
		layout: TextLayoutResult,
		color: string,
		halign: HorizontalAlignment,
		valign: VerticalAlignment,
		outlineStyle: LineStyle | undefined,
		underline: boolean,
		strikethrough: boolean
	): void {
		if (layout.lines.length < 1) return

		// Set font for rendering
		this.context2d.font = layout.fontDefinition

		let xAnchor = x
		switch (halign) {
			case 'left':
				this.context2d.textAlign = 'left'
				xAnchor = x
				break
			case 'center':
				this.context2d.textAlign = 'center'
				xAnchor = x + w / 2
				break
			case 'right':
				this.context2d.textAlign = 'right'
				xAnchor = x + w
				break
		}

		// how many lines can we fit?
		const numLines = Math.min(Math.max(Math.floor((h + h * 1e-4) / layout.measuredLineHeight), 1), layout.lines.length) // no Linespacing, make h a little taller to compensate for rounding errors around the breakpoints, draw at least one line even if it is too big and maximum the lines that really are there

		const drawingLinesTotalHeight = layout.measuredLineHeight * numLines
		let yAnchor = 0
		switch (valign) {
			case 'top':
				yAnchor = layout.measuredAscent
				break
			case 'center':
				yAnchor = (h - drawingLinesTotalHeight) / 2 + layout.measuredAscent
				break
			case 'bottom':
				yAnchor = h - drawingLinesTotalHeight + layout.measuredAscent
				break
		}
		yAnchor += y

		this.context2d.fillStyle = color
		if (outlineStyle && outlineStyle.width > 0) {
			this.context2d.strokeStyle = outlineStyle.color
			this.context2d.lineWidth = outlineStyle.width
		}

		// only draw the lines that actually fit in the box (numLines); the rest overflow and are clipped
		for (const line of layout.lines.slice(0, numLines)) {
			if (outlineStyle && outlineStyle.width > 0) {
				this.context2d.strokeText(line.text, xAnchor, yAnchor)
			}

			this.context2d.fillText(line.text, xAnchor, yAnchor)

			// Underline/strikethrough have no native canvas support, so draw them manually.
			// fillStyle is still `color` here, so the decorations match the text color.
			if (underline || strikethrough) {
				const metrics = this.context2d.measureText(line.text)
				const width = metrics.width
				if (width > 0) {
					const ascent = metrics.fontBoundingBoxAscent
					const descent = metrics.fontBoundingBoxDescent
					const thickness = Math.max(1, (ascent + descent) / 14)
					// textAlign is set above, so derive the line's left edge from the anchor.
					const left = halign === 'center' ? xAnchor - width / 2 : halign === 'right' ? xAnchor - width : xAnchor
					if (underline) {
						this.context2d.fillRect(left, yAnchor + descent * 0.5, width, thickness)
					}
					if (strikethrough) {
						this.context2d.fillRect(left, yAnchor - ascent * 0.3, width, thickness)
					}
				}
			}

			//this.horizontalLine(yAnchor - fontsize, 'rgb(255,0,255)')
			// this.horizontalLine(yAnchor + correctedDescent, 'rgb(0, 255, 0)')
			// this.horizontalLine(yAnchor + line.descent, 'rgb(0, 255, 0)')
			// this.horizontalLine(yAnchor - correctedAscent, 'rgb(0,0,255)')
			// this.horizontalLine(yAnchor, 'rgb(255, 0, 0)')
			//console.log('Fontsize', fontsize, 'Lineheight', lineheight, 'asc', correctedAscent, 'des', correctedDescent, 'a+d', correctedAscent+correctedDescent);

			yAnchor += layout.measuredLineHeight
		}
	}

	/**
	 * drawPixeBuffer
	 *
	 * Buffer can be either a buffer, or base64 encoded string.
	 * Type can be set to either 'buffer' or 'base64' according to your input data.
	 * Width and height is information about your buffer, not scaling.
	 *
	 * The buffer data is expected to be RGB or ARGB data, 1 byte per color,
	 * horizontally. Top left is first three bytes.
	 *
	 * @param x left position of insert point
	 * @param y top position of insert point
	 * @param width integer information of the width of the buffer
	 * @param height integer information of the height of the buffer
	 * @param bufferRaw
	 * @param format pixel format of the buffer, if known
	 * @param scale scaling factor
	 */
	drawPixelBuffer(
		x: number,
		y: number,
		width: number,
		height: number,
		bufferRaw: Uint8Array | string,
		format?: 'RGB' | 'RGBA' | 'ARGB',
		scale?: number
	): void {
		let buffer: Buffer
		if (typeof bufferRaw == 'object') {
			if (bufferRaw instanceof Buffer) {
				buffer = bufferRaw
			} else if (bufferRaw instanceof Uint8Array) {
				buffer = Buffer.from(bufferRaw.buffer, bufferRaw.byteOffset, bufferRaw.byteLength)
			} else {
				this.logger.error(`Pixelbuffer is of unknown type (${typeof bufferRaw})`)
				return
			}
		} else if (typeof bufferRaw == 'string') {
			buffer = Buffer.from(bufferRaw, 'base64')
		} else {
			this.logger.error(`Pixelbuffer is of unknown type (${typeof bufferRaw})`)
			return
		}

		const rgbByteCount = width * height * 3
		const rgbaByteCount = width * height * 4

		if (format == 'RGB' && buffer.length !== rgbByteCount) {
			this.logger.error(
				`Pixelbuffer of format ${format} with ${buffer.length} bytes is not expected ${rgbByteCount} bytes`
			)
			return
		} else if ((format == 'RGBA' || format == 'ARGB') && buffer.length !== rgbaByteCount) {
			this.logger.error(
				`Pixelbuffer of format ${format} with ${buffer.length} bytes is not expected ${rgbaByteCount} bytes`
			)
			return
		} else if (!format && buffer.length < rgbByteCount) {
			this.logger.error(`Pixelbuffer of ${buffer.length} bytes is not expected ${rgbByteCount} bytes`)
			return
		}

		if (buffer.length == rgbaByteCount && format === 'RGBA') {
			// Buffer is packed ok
		} else if (buffer.length == rgbaByteCount && (!format || format === 'ARGB')) {
			// ARGB: swap order from ARGB to RGBA
			const argb = Uint8Array.from(buffer)

			buffer = Buffer.alloc(width * height * 4)
			for (let i = 0; i < buffer.length; i += 4) {
				const a = argb[i]

				buffer[i] = argb[i + 1]
				buffer[i + 1] = argb[i + 2]
				buffer[i + 2] = argb[i + 3]
				buffer[i + 3] = a
			}
		} else if (buffer.length == rgbByteCount) {
			// RGB: add alpha channel
			const rgb = Uint8Array.from(buffer)

			buffer = Buffer.alloc(width * height * 4)
			for (let i = 0; i < rgb.length / 3; i += 1) {
				buffer[i * 4] = rgb[i * 3] // Red
				buffer[i * 4 + 1] = rgb[i * 3 + 1] // Green
				buffer[i * 4 + 2] = rgb[i * 3 + 2] // Blue
				buffer[i * 4 + 3] = 255 // Alpha
			}
		} else {
			this.logger.error(
				`Pixelbuffer for a ${width}x${height} image should be either ${rgbByteCount} or ${
					rgbaByteCount
				} bytes big. Not ${buffer.length} (${format})`
			)
			return
		}

		// create HTML compatible imageData object
		const imageCanvas = this.loadPixelBuffer(buffer, width, height)
		if (!imageCanvas) return

		if (!scale) scale = 1

		this.drawImage(imageCanvas, 0, 0, width, height, x, y, width * scale, height * scale)
	}

	/**
	 * Draws a triangle in a corner
	 * @param depth
	 * @param color CSS color string
	 * @param halign defaults to 'left'
	 * @param valign defaults to 'top'
	 * @returns success
	 */
	/*
	drawCornerTriangle(
		depth: number,
		color: string,
		halign: HorizontalAlignment = 'left',
		valign: VerticalAlignment = 'top'
	): boolean {
		if (depth == 0) return false
		let points: PointXY[] | undefined

		if (halign == 'left' && valign == 'top') {
			points = [
				[0, 0],
				[depth, 0],
				[0, depth],
			]
		}
		if (halign == 'right' && valign == 'top') {
			points = [
				[this.width, 0],
				[this.width - depth, 0],
				[this.width, depth],
			]
		}
		if (halign == 'right' && valign == 'bottom') {
			points = [
				[this.width, this.height],
				[this.width - depth, this.height],
				[this.width, this.height - depth],
			]
		}
		if (halign == 'left' && valign == 'bottom') {
			points = [
				[0, this.height],
				[depth, this.height],
				[0, this.height - depth],
			]
		}
		if (!points) return false

		return this.drawFilledPath(points, color)
	}
		*/

	/**
	 * Draws an outline path from some points
	 * @param pathPoints an array of x and y points
	 * @param color CSS color
	 * @param lineWidth defaults to 1
	 * @param close if true close the path from last point to first point, if last and first point are identical path will be autoclosed
	 * @returns success
	 */
	drawPath(pathPoints: PointXY[], lineStyle: LineStyle, close = false): boolean {
		const lineWidth = lineStyle.width ?? 1

		if (lineWidth <= 0) return false
		if (!Array.isArray(pathPoints) || pathPoints.length == 0) return false
		this.context2d.beginPath()
		const [firstPoint, ...points] = pathPoints
		this.context2d.moveTo(firstPoint[0], firstPoint[1])
		points.forEach((point) => {
			this.context2d.lineTo(point[0], point[1])
		})

		const lastPoint = points[points.length - 1]
		if (close || (lastPoint && firstPoint[0] == lastPoint[0] && firstPoint[1] == lastPoint[1])) {
			this.context2d.closePath()
		}

		this.context2d.strokeStyle = lineStyle.color
		this.context2d.lineWidth = lineWidth
		this.context2d.stroke()
		return true
	}

	/**
	 * Draws a filled path from some points
	 * @param pathPoints an array of x and y points
	 * @param color CSS color
	 * @returns success
	 */
	drawFilledPath(pathPoints: PointXY[], color: string): boolean {
		if (!Array.isArray(pathPoints) || pathPoints.length == 0) return false
		this.context2d.beginPath()
		const [firstPoint, ...points] = pathPoints
		this.context2d.moveTo(firstPoint[0], firstPoint[1])
		points.forEach((point) => {
			this.context2d.lineTo(point[0], point[1])
		})

		this.context2d.fillStyle = color
		this.context2d.fill()
		return true
	}
}

/**
 * Trim a base64 image string to be valid base64. A malformed string with `=` padding in the
 * middle is decoded by skia-canvas (up to the padding), but rejected outright by browsers.
 * Trimming at the first `=` and re-padding makes both renderers behave consistently.
 */
export function trimBase64ImageToValid(base64Image: string): string {
	// Skip past any `data:image/...;base64,` prefix, only searching within the encoded body
	const dataUrlMatch = /^data:[^,]*,/.exec(base64Image)
	const bodyStart = dataUrlMatch ? dataUrlMatch[0].length : 0

	const paddingIndex = base64Image.indexOf('=', bodyStart)
	if (paddingIndex === -1) return base64Image // No padding present, nothing to trim

	// Everything from the first `=` onwards must be padding, so discard any junk that follows it.
	// A final base64 group is 4 chars, so keep the data plus the `=`(s) that complete that group.
	const remainder = (paddingIndex - bodyStart) % 4
	return base64Image.slice(0, paddingIndex + ((4 - remainder) % 4))
}
